/**
 * InstallGuide - "put ChoreConnect on your phone"
 * -----------------------------------------------
 * Step-by-step instructions for adding the site to a home screen, and for
 * letting it send notifications. Shown to everybody - students AND the people
 * posting chores - in two places: once just after signing up, and permanently
 * in Settings.
 *
 * WHY THIS IS WORTH A WHOLE COMPONENT
 * Almost everybody uses this on a phone, and two of the site's better ideas
 * are invisible without it. A student who has not installed it does not get
 * told when a job appears, and on an iPhone that is not a setting they can
 * find - Apple only allows notifications for a site added to the home screen,
 * and nothing in Safari says so. Left to work it out, nobody does.
 *
 * WHY IT GUESSES THE DEVICE, AND WHY YOU CAN STILL CHANGE IT
 * "Tap the share button" and "tap the three dots" are different enough that
 * showing all three at once is worse than showing one: people follow the
 * wrong set, fail, and conclude the site is broken. So the detected device
 * leads - see devicePlatform() in services/push.js.
 *
 * But the other two are one tap away, because guessing is not always right
 * and is not always what somebody wants. Two real cases: an iPad, which
 * reports itself as a Mac and so used to be handed desktop instructions that
 * do not work; and anybody reading this on a laptop who is trying to set up
 * the phone in their other hand.
 *
 * The steps name the buttons exactly as the phone labels them - "Add to Home
 * Screen", not "the add option" - because somebody following this is looking
 * at a menu and matching words.
 */

import { useEffect, useState } from 'react';
import { Share, MoreVertical, Plus, Bell, Check, Smartphone, Monitor, AlertTriangle } from 'lucide-react';

import { pushSupport, askAndSubscribe, isSubscribed, devicePlatform, isInstalled } from '../services/push';

import { useI18n } from '../hooks/useI18n';

/**
 * One list per platform. Each step is what to tap, in order, with the words
 * the phone itself uses.
 */
const DEVICES = [
  { key: 'ios', label: 'install.deviceIos', icon: Share },
  { key: 'android', label: 'install.deviceAndroid', icon: MoreVertical },
  { key: 'desktop', label: 'install.deviceDesktop', icon: Monitor },
];

// `t` is passed in rather than read from the hook: this is a plain
// function, not a component, and the guide is chosen before it renders.
function stepsFor(platform, installed, t) {
  if (platform === 'ios') {
    return {
      icon: Share,
      title: t('install.addToHome'),
      // On an iPhone or iPad this is not optional if they want notifications
      // at all - which is why this guide exists.
      why: t('install.iosWhy'),
      note: t('install.iosNote'),
      steps: [
        t('install.ios1'), t('install.ios2'), t('install.ios3'),
        t('install.ios4'), t('install.ios5'), t('install.ios6'),
      ],
    };
  }

  if (platform === 'android') {
    return {
      icon: MoreVertical,
      title: t('install.addToHome'),
      why: t('install.androidWhy'),
      note: t('install.androidNote'),
      steps: [
        t('install.android1'), t('install.android2'), t('install.android3'),
        t('install.android4'), t('install.android5'),
      ],
    };
  }

  return {
    icon: Monitor,
    title: t('install.desktopTitle'),
    why: t('install.desktopWhy'),
    note: t('install.desktopNote'),
    steps: [
      t('install.desktop1'),
      t('install.desktop2'), t('install.desktop3'), t('install.desktop4'),
    ],
  };
}

export default function InstallGuide({ withNotifications = true, startOpen = false }) {
  const { t } = useI18n();
  const [platform, setPlatform] = useState('desktop');
  const [installed, setInstalled] = useState(false);
  const [support, setSupport] = useState({ ok: false, reason: 'checking' });
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setPlatform(devicePlatform());
    setInstalled(isInstalled());
    setSupport(pushSupport());
    isSubscribed().then(setSubscribed);
  }, []);

  const turnOn = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    const result = await askAndSubscribe();
    setBusy(false);

    if (result.ok) {
      setSubscribed(true);
      setNotice(t('install.notificationsOn'));
      return;
    }

    setError(
      {
        refused: 'Your browser said no. You can allow it again from the padlock icon next to the address.',
        blocked: 'Notifications are blocked for this site. Allow them from the padlock icon next to the address, then reload.',
        'ios-needs-install': 'Add ChoreConnect to your home screen first, using the steps above.',
        'server-off': 'The server is not set up to send notifications yet.',
        unsupported: 'This browser cannot show notifications.',
      }[result.reason] || 'That did not work.'
    );
  };

  const guide = stepsFor(platform, installed, t);
  const Icon = guide.icon;

  // Already on the home screen, on the device where that mattered. There is
  // nothing to instruct - saying so is more use than a list they have done.
  const alreadyDone = installed && platform !== 'desktop';

  /**
   * THE STEPS STAY SHUT UNTIL SOMEBODY TAPS THEIR DEVICE.
   *
   * Changed 24 August 2026. Everything used to be open at once - the reason,
   * three device buttons, five numbered steps and a caveat - which on a
   * laptop is a tidy card and on a phone is the whole screen, shown to
   * somebody who has just signed up and has not seen the site yet.
   *
   * The detected device is still the one highlighted, so the right button is
   * obvious and it is one tap rather than a decision. What changed is only
   * that the instructions wait to be asked for.
   *
   * `startOpen` is for the Settings page, where somebody has deliberately
   * gone looking for this and should not have to ask twice.
   */
  const [revealed, setRevealed] = useState(startOpen);

  return (
    // The id is a link target: when notifications turn out to be unavailable,
    // NotificationSettings points here rather than drawing a second copy of
    // these steps a few centimetres further down the same page.
    <div className="card install-guide" id="install-guide">
      <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
        <span className="feature-icon" style={{ marginBottom: 0, flexShrink: 0 }}>
          <Smartphone size={24} aria-hidden="true" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{t('install.getOnYourPhone')}</h3>
          <p className="text-sm text-muted" style={{ marginTop: 2 }}>
            {t('install.twoMinutesOnce')}
          </p>
        </div>
      </div>

      <hr className="divider" />

      {/* ---------------- 0. WHICH DEVICE ----------------
          The detected one is selected already, so most people never touch
          this. It is here because detection is not always right and not
          always what somebody wants - an iPad reports itself as a Mac, and
          anybody reading this on a laptop is probably setting up the phone in
          their other hand. */}
      <div style={{ marginBottom: 'var(--space-sm)' }}>
        <span className="label">{t('install.whichDevice')}</span>
        <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap', marginTop: 4 }}>
          {DEVICES.map((d) => (
            <button
              key={d.key}
              type="button"
              className={`btn btn-sm btn-inline ${platform === d.key ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => {
                setPlatform(d.key);
                setRevealed(true);
              }}
              aria-pressed={platform === d.key}
              aria-expanded={revealed && platform === d.key}
            >
              <d.icon size={15} aria-hidden="true" />
              {t(d.label)}
            </button>
          ))}
        </div>
      </div>

      {/* ---------------- 1. THE HOME SCREEN ----------------
          Nothing below appears until a device has been tapped. See the note
          by `revealed` above. */}
      {!revealed && !alreadyDone && (
        <p className="text-sm text-muted" style={{ margin: 0 }}>
          {t('install.chooseToSeeHow')}
        </p>
      )}

      {revealed && (alreadyDone ? (
        <div className="alert alert-success">
          <Check size={18} aria-hidden="true" />
          <div>
            <strong>{t('misc.alreadyInstalled')}</strong> {t('install.nothingToDo')}
          </div>
        </div>
      ) : (
        <>
          <div className="row" style={{ gap: 8, marginBottom: 6 }}>
            <Icon size={18} className="text-green" aria-hidden="true" />
            <strong>{guide.title}</strong>
          </div>

          <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
            {guide.why}
          </p>

          <ol className="install-steps">
            {guide.steps.map((step, i) => (
              <li key={step}>
                <span className="install-step-number" aria-hidden="true">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>

          {guide.note && (
            <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
              <AlertTriangle size={13} style={{ display: 'inline', verticalAlign: -2 }} aria-hidden="true" />{' '}
              {guide.note}
            </p>
          )}
        </>
      ))}

      {/* ---------------- 2. NOTIFICATIONS ---------------- */}
      {withNotifications && (
        <>
          <hr className="divider" />

          <div className="row" style={{ gap: 8, marginBottom: 6 }}>
            <Bell size={18} className="text-green" aria-hidden="true" />
            <strong>{t('misc.allowNotifications')}</strong>
          </div>

          {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-xs)' }}>{notice}</div>}
          {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-xs)' }}>{error}</div>}

          {subscribed ? (
            <p className="text-sm text-muted">
              {t('install.onForThisDevice')}
            </p>
          ) : support.reason === 'ios-needs-install' ? (
            <p className="text-sm text-muted">
              {t('install.buttonAppearsWhen')}
            </p>
          ) : support.reason === 'blocked' ? (
            <div className="alert alert-warning">
              <AlertTriangle size={18} aria-hidden="true" />
              <div className="text-sm">
                <strong>{t('misc.blockedHere')}</strong> {t('install.blockedBodyShort')}
              </div>
            </div>
          ) : support.reason === 'unsupported' ? (
            <p className="text-sm text-muted">{t('misc.browserCannot')}</p>
          ) : (
            <>
              <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-xs)' }}>
                {t('install.phoneWillAsk')}
              </p>
              <button
                type="button"
                className="btn btn-primary btn-sm btn-inline"
                onClick={turnOn}
                disabled={busy}
              >
                <Bell size={16} /> {busy ? t('alerts.justAMoment') : t('alerts.turnOn')}
              </button>
            </>
          )}
        </>
      )}

      <p className="hint" style={{ marginTop: 'var(--space-sm)' }}>
        <Plus size={13} style={{ display: 'inline', verticalAlign: -2 }} aria-hidden="true" />{' '}
        {t('install.perDeviceShort')}
      </p>
    </div>
  );
}
